/**
 * Checklist Item Row Component
 * 
 * Displays a single checklist item with completion toggle, due date, and source badge
 */

import { View, Pressable } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { ThemedText, Body, Caption } from '@/components/ui/Text';
import type { ChecklistItem } from '@/services/checklists';

type ChecklistItemRowProps = {
  item: ChecklistItem;
  onToggle: (itemId: string) => void;
  onPress?: (item: ChecklistItem) => void;
};

export function ChecklistItemRow({ item, onToggle, onPress }: ChecklistItemRowProps) {
  const theme = useTheme();

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getSourceBadgeColor = () => {
    switch (item.source) {
      case 'booking':
        return theme.colors.primary;
      case 'document':
        return theme.colors.success;
      case 'weather':
        return theme.colors.info || theme.colors.primary;
      case 'city_context':
        return theme.colors.primary;
      case 'travel_requirements':
        return theme.colors.warning;
      default:
        return theme.colors.textSecondary;
    }
  };

  const getSourceIcon = () => {
    switch (item.source) {
      case 'booking':
        return 'calendar-outline';
      case 'document':
        return 'document-text-outline';
      case 'weather':
        return 'cloud-outline';
      case 'city_context':
        return 'location-outline';
      case 'travel_requirements':
        return 'shield-checkmark-outline';
      default:
        return 'checkmark-circle-outline';
    }
  };

  const isOverdue = item.dueDate && new Date(item.dueDate) < new Date() && !item.completed;

  return (
    <Pressable
      onPress={() => onPress?.(item)}
      onLongPress={() => onToggle(item.id)}
      style={({ pressed }) => ({
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: theme.spacing(1.25),
        padding: theme.spacing(1.25),
        backgroundColor: item.completed
          ? theme.colors.primary + '08'
          : isOverdue
            ? theme.colors.error + '08'
            : theme.colors.surface,
        borderRadius: theme.radii.md,
        borderWidth: 1,
        borderColor: item.completed
          ? theme.colors.primary + '25'
          : isOverdue
            ? theme.colors.error + '25'
            : theme.colors.borderMuted,
        opacity: pressed ? 0.7 : 1,
      })}
    >
      <Pressable
        onPress={() => onToggle(item.id)}
        style={{
          width: 24,
          height: 24,
          borderRadius: theme.radii.sm,
          borderWidth: 2,
          borderColor: item.completed
            ? theme.colors.primary
            : theme.colors.borderMuted,
          backgroundColor: item.completed
            ? theme.colors.primary
            : 'transparent',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: 2,
        }}
      >
        {item.completed && (
          <Ionicons name="checkmark" size={16} color={theme.colors.surface} />
        )}
      </Pressable>

      <View style={{ flex: 1, gap: theme.spacing(0.5) }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1), flexWrap: 'wrap' }}>
          <ThemedText
            variant="body"
            style={{
              flex: 1,
              textDecorationLine: item.completed ? 'line-through' : 'none',
              opacity: item.completed ? 0.6 : 1,
            }}
          >
            {item.title}
          </ThemedText>
          
          {item.autoGenerated && item.source && (
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: theme.spacing(0.25),
                paddingHorizontal: theme.spacing(0.75),
                paddingVertical: theme.spacing(0.25),
                backgroundColor: getSourceBadgeColor() + '20',
                borderRadius: theme.radii.sm,
              }}
            >
              <Ionicons
                name={getSourceIcon() as any}
                size={12}
                color={getSourceBadgeColor()}
              />
            </View>
          )}
        </View>

        {item.dueDate && (
          <Caption
            color={isOverdue ? theme.colors.error : theme.colors.textSecondary}
            style={{ fontSize: 12 }}
          >
            Due: {formatDate(item.dueDate)}
            {isOverdue && ' (Overdue)'}
          </Caption>
        )}

        {item.notes && (
          <Caption color={theme.colors.textSecondary} style={{ fontSize: 12 }}>
            {item.notes}
          </Caption>
        )}
      </View>
    </Pressable>
  );
}

